import React, { useState } from 'react';
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Button,
	Chip,
	Typography,
	CircularProgress,
	Alert,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	Tooltip,
	Tabs,
	Tab,
	Radio,
	RadioGroup,
	FormControlLabel,
} from '@mui/material';
import DialogWrapper from '@/components/DialogWrapper';
import {
	CheckCircle as ApproveIcon,
	Cancel as DenyIcon,
	Refresh as RefreshIcon,
	Close as CloseIcon,
} from '@mui/icons-material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import {
	PermissionRequest,
	RequestStatus,
} from '@/api/permissionRequests';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissionRequestsQuery } from '@/features/permissionRequests/hooks/usePermissionRequestsQuery';
import { useApprovePermissionRequestMutation, useDenyPermissionRequestMutation } from '@/features/permissionRequests/hooks/usePermissionRequestsMutations';
import { queryKeys } from '@/lib/queryKeys';
import { formatDistanceToNow } from 'date-fns';
import { QueryErrorState, QueryStaleDataWarning } from '@/components/state';
import { useAppStore } from '@/store';

interface PermissionRequestsTabProps {
	isAdmin?: boolean;
	isManager?: boolean;
}

type DecisionAction = 'skip' | 'approve' | 'deny';

const PermissionRequestsTab: React.FC<PermissionRequestsTabProps> = ({ isAdmin = false, isManager = false }) => {
	const isAdminOrManager = isAdmin || isManager;
	const queryClient = useQueryClient();
	const refreshPendingPermissionRequestsCount = useAppStore((state) => state.refreshPendingPermissionRequestsCount);
	
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [selectedStatus, setSelectedStatus] = useState<RequestStatus>('pending');

	const [selectedGroup, setSelectedGroup] = useState<any | null>(null);

	const {
		data,
		isLoading,
		isError: isQueryError,
		isRefetchError,
		error: queryError,
	} = usePermissionRequestsQuery(
		selectedStatus, isAdminOrManager, true
	);
	const requests = !isAdminOrManager && data && 'requests' in data ? data.requests : [];
    const groups = isAdminOrManager && data && 'groups' in data ? data.groups : null;
	const hasCachedData = Boolean((groups && groups.length > 0) || (!groups && requests.length > 0));
	const queryErrorMessage = isQueryError ? ((queryError as Error)?.message || 'Failed to load permission requests') : null;
	const showStaleDataWarning = Boolean(isRefetchError && hasCachedData && queryErrorMessage);
	const blockingQueryError = hasCachedData ? null : queryErrorMessage;

	// Dialog states
	const [approveDialogOpen, setApproveDialogOpen] = useState(false);
	const [denyDialogOpen, setDenyDialogOpen] = useState(false);
	const [selectedRequest, setSelectedRequest] = useState<PermissionRequest | null>(null);
	const [approvalNote, setApprovalNote] = useState('');
	const [denialReason, setDenialReason] = useState('');

	const approveMutation = useApprovePermissionRequestMutation();
	const denyMutation = useDenyPermissionRequestMutation();

	// Display approve/deny errors separately or combined
	const mutationError = approveMutation.error || denyMutation.error;

	    // Bulk decision tracking
    const [decisions, setDecisions] = useState<Map<string, DecisionAction>>(new Map());
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);


	const handleApproveClick = (request: PermissionRequest) => {
		setSelectedRequest(request);
		setApprovalNote('');
		setApproveDialogOpen(true);
	};

	const handleDenyClick = (request: PermissionRequest) => {
		setSelectedRequest(request);
		setDenialReason('');
		setDenyDialogOpen(true);
	};

	const handleApprove = async () => {
		if (!selectedRequest) return;
		
		approveMutation.mutate(
			{ requestId: selectedRequest.id, data: { note: approvalNote || undefined } },
			{
				onSuccess: async () => {
					setSuccessMessage(`Approved permission request for ${selectedRequest.permission}`);
					setApproveDialogOpen(false);
					await refreshPendingPermissionRequestsCount();
				},
				onError: (err: any) => {
					console.error('Failed to approve request:', err);
					//setError(err.response?.data?.detail || 'Failed to approve request');
				},
			}
		);
	};

	const handleDeny = async () => {
		if (!selectedRequest || !denialReason.trim()) return;
		
		denyMutation.mutate(
			{ requestId: selectedRequest.id, data: { reason: denialReason } },
			{
				onSuccess: async () => {
					setSuccessMessage(`Denied permission request for ${selectedRequest.permission}`);
					setDenyDialogOpen(false);
					await refreshPendingPermissionRequestsCount();
				},
				onError: (err: any) => {
					console.error('Failed to deny request:', err);
					//setError(err.response?.data?.detail || 'Failed to deny request');
				},
			}
		);
	};

	const getStatusColor = (status: RequestStatus): 'default' | 'warning' | 'success' | 'error' => {
		switch (status) {
			case 'pending':
				return 'warning';
			case 'approved':
				return 'success';
			case 'denied':
				return 'error';
			case 'cancelled':
				return 'default';
			default:
				return 'default';
		}
	};

	    const handleDecisionChange = (requestId: string, action: DecisionAction) => {
        setDecisions((prev) => {
            const updated = new Map(prev);
            if (action === 'skip') {
                updated.delete(requestId);
            } else {
                updated.set(requestId, action);
            }
            return updated;
        });
    };

    const getDecisionCounts = () => {
        const counts = { approve: 0, deny: 0 };
        decisions.forEach((action) => {
            if (action === 'approve') counts.approve++;
            if (action === 'deny') counts.deny++;
        });
        return counts;
    };

    const hasChanges = decisions.size > 0;

    const handleSaveClick = () => {
        if (!hasChanges) return;
        setConfirmDialogOpen(true);
    };

    const handleBulkProcess = async () => {
        try {
            const errors: string[] = [];
			const decisionCounts = getDecisionCounts();

            // Process all decisions
            for (const [requestId, action] of decisions.entries()) {
                try {
                    if (action === 'approve') {
                        await new Promise((resolve, reject) => {
                            approveMutation.mutate(
                                { requestId, data: {} },
                                { onSuccess: resolve as any, onError: reject }
                            );
                        });
                    } else if (action === 'deny') {
                        await new Promise((resolve, reject) => {
                            denyMutation.mutate(
                                { requestId, data: { reason: 'Bulk denial by manager' } },
                                { onSuccess: resolve as any, onError: reject }
                            );
                        });
                    }
                } catch (err: any) {
                    errors.push(`Failed to ${action} request ${requestId}: ${err.message}`);
                }
            }

            setConfirmDialogOpen(false);
            setDecisions(new Map());
			await refreshPendingPermissionRequestsCount();

            if (errors.length === 0) {
				const { approve, deny } = decisionCounts;
                setSuccessMessage(
                    `Successfully processed ${approve + deny} request(s): ${approve} approved, ${deny} denied`
                );
            }
        } catch (err: any) {
            console.error('Failed to process bulk requests:', err);
        }
    };


	const formatDate = (dateString: string) => {
		try {
			return formatDistanceToNow(new Date(dateString), { addSuffix: true });
		} catch {
			return dateString;
		}
	};

	const canApprove = isAdmin || isManager;
	const filteredRequests = requests || [];

	const retryPermissionRequestsQuery = async () => {
		await queryClient.invalidateQueries({ queryKey: queryKeys.permissionRequests.all });
		await refreshPendingPermissionRequestsCount();
	};

	return (
		<Box>
			{/* Header (no title) */}
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0 }}>
				<Tooltip title="Refresh">
					<span>
						<IconButton
							size="small"
							color="primary"
							aria-label="refresh-requests"
							disabled={isLoading}
							onClick={() => {
								void retryPermissionRequestsQuery();
							}}
							
						>
							<RefreshIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Box>

			{/* Status Filter Tabs */}
			<Tabs
				value={selectedStatus}
				onChange={(_, newValue) => setSelectedStatus(newValue)}
				sx={{ mb: 2, mt: 0 }}
			>
				<Tab label="Pending" value="pending" />
				<Tab label="Approved" value="approved" />
				<Tab label="Denied" value="denied" />
			</Tabs>

			{/* Success Message */}
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
					{successMessage}
				</Alert>
			)}

			{showStaleDataWarning && (
				<Box sx={{ mb: 2 }}>
					<QueryStaleDataWarning
						message="Showing last synced permission requests. We couldn't refresh the latest data."
						onRetry={() => {
							void retryPermissionRequestsQuery();
						}}
					/>
				</Box>
			)}

			{/* Error Message */}
			{mutationError && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => {}}>
					{(mutationError as any)?.message}
				</Alert>
			)}

			{/* Loading State */}
			{isLoading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
					<CircularProgress />
				</Box>
			) : blockingQueryError ? (
				<QueryErrorState
					title="Unable to load permission requests"
					message={blockingQueryError}
					onRetry={() => {
						void retryPermissionRequestsQuery();
					}}
					minHeight={220}
				/>
			) : (groups && groups.length === 0) || (!groups && filteredRequests.length === 0) ? (
				<Paper sx={{ p: 3, textAlign: 'center' }}>
					<Typography color="text.secondary">
						No {selectedStatus} permission requests
					</Typography>
				</Paper>
			) : (
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold' }}>Requester</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>Request date</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>#Pending</TableCell>
								{canApprove && <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>}
							</TableRow>
						</TableHead>
						<TableBody>
							{groups ? (
								groups.map((g, idx) => (
									<TableRow key={g.user_id ?? `group-${idx}`}>
										<TableCell>
											<Typography variant="body2">{g.user_name || g.user_id}</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2">{g.user_email || 'N/A'}</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{g.latest_requested_at ? formatDate(g.latest_requested_at) : 'N/A'}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2">{g.pending_count || 0}</Typography>
										</TableCell>
										{canApprove && (
											<TableCell align="center">
												<Tooltip title="View Requests">
													<span>
														<IconButton size="small" onClick={() => setSelectedGroup(g)}>
															<VisibilityIcon />
														</IconButton>
													</span>
												</Tooltip>
											</TableCell>
										)}
									</TableRow>
								))
							) : (
								filteredRequests.map((request, idx) => (
									<TableRow key={request.id ?? (request as any).request_id ?? `${request.requester_id ?? 'req'}-${idx}`}>
										<TableCell>
											<Typography variant="body2">
												{(request.requester_id?.substring(0, 8) || request.user_id?.substring(0, 8) || 'unknown')}...
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
												{request.permission}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" sx={{ maxWidth: 300 }}>
												{request.justification}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2">{request.business_unit || 'N/A'}</Typography>
										</TableCell>
										<TableCell>
											<Chip label={request.status} color={getStatusColor(request.status)} size="small" />
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{request.requested_at ? formatDate(request.requested_at) : 'Unknown'}
											</Typography>
										</TableCell>
										{canApprove && (
											<TableCell align="center">
												{request.status === 'pending' ? (
													<Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
														<Tooltip title="Approve">
															<span>
																<IconButton size="small" color="success" onClick={() => handleApproveClick(request)}>
																	<ApproveIcon />
																</IconButton>
															</span>
														</Tooltip>
														<Tooltip title="Deny">
															<span>
																<IconButton size="small" color="error" onClick={() => handleDenyClick(request)}>
																	<DenyIcon />
																</IconButton>
															</span>
														</Tooltip>
													</Box>
												) : (
													<Typography variant="body2" color="text.secondary">
														{request.status === 'approved'
															? `Approved ${formatDate(request.approved_at || '')}`
															: request.status === 'denied'
															? `Denied ${formatDate(request.denied_at || '')}`
															: 'Cancelled'}
													</Typography>
												)}
											</TableCell>
										)}
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{/* Approve Dialog */}
	<DialogWrapper open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Approve Permission Request</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 2 }}>
						You are about to approve the following permission request:
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
						Permission: <code>{(selectedRequest as any)?.permission_display || selectedRequest?.permission}</code>
					</Typography>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Justification: {selectedRequest?.justification}
					</Typography>
					<TextField
						label="Approval Note (Optional)"
						multiline
						rows={3}
						fullWidth
						value={approvalNote}
						onChange={(e) => setApprovalNote(e.target.value)}
						placeholder="Add a note for the requester..."
						sx={{ mt: 2 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setApproveDialogOpen(false)} disabled={approveMutation.isPending} color="primary" variant="outlined">
						Cancel
					</Button>
					<Button
						onClick={handleApprove}
						variant="contained"
						color="success"
						disabled={approveMutation.isPending}
						startIcon={approveMutation.isPending ? <CircularProgress size={20} /> : <ApproveIcon />}
					>
						Approve
					</Button>
				</DialogActions>
	</DialogWrapper>

			{/* Deny Dialog */}
	<DialogWrapper open={denyDialogOpen} onClose={() => setDenyDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Deny Permission Request</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 2 }}>
						You are about to deny the following permission request:
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
						Permission: <code>{(selectedRequest as any)?.permission_display || selectedRequest?.permission}</code>
					</Typography>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Justification: {selectedRequest?.justification}
					</Typography>
					<TextField
						label="Denial Reason (Required)"
						multiline
						rows={3}
						fullWidth
						value={denialReason}
						onChange={(e) => setDenialReason(e.target.value)}
						placeholder="Please provide a reason for denial..."
						required
						error={denialReason.trim().length === 0}
						helperText={denialReason.trim().length === 0 ? 'Reason is required' : ''}
						sx={{ mt: 2 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDenyDialogOpen(false)} disabled={denyMutation.isPending} color="primary" variant="outlined">
						Cancel
					</Button>
					<Button
						onClick={handleDeny}
						variant="contained"
						color="error"
						disabled={denyMutation.isPending || !denialReason.trim()}
						startIcon={denyMutation.isPending ? <CircularProgress size={20} /> : <DenyIcon />}
					>
						Deny
					</Button>
				</DialogActions>
	</DialogWrapper>

			{/* Requester Detail Modal */}
			<DialogWrapper open={!!selectedGroup} onClose={() => setSelectedGroup(null)} maxWidth="md" fullWidth>
				<DialogTitle>
					Permission Requests - {selectedGroup?.user_name || selectedGroup?.user_id}
					<IconButton
						aria-label="close"
						onClick={() => setSelectedGroup(null)}
						sx={{ position: 'absolute', right: 8, top: 8 }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					{selectedGroup && (
						<>
							{/* Requester Info */}
							<Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
								<Typography variant="subtitle2" color="text.secondary" gutterBottom>
									Requester Information
								</Typography>
								<Typography variant="body2">
									<strong>Name:</strong> {selectedGroup.user_name || 'N/A'}
								</Typography>
								<Typography variant="body2">
									<strong>Email:</strong> {selectedGroup.user_email || 'N/A'}
								</Typography>
								{selectedGroup.business_unit && (
									<Typography variant="body2">
										<strong>Business Unit:</strong> {selectedGroup.business_unit}
									</Typography>
								)}
							</Box>

							{/* Pending Requests List */}
							{(() => {
								const pendingRequests = (selectedGroup.requests || []).filter((req: any) => req.status === 'pending');
								return pendingRequests.length > 0 ? (
									<>
										<Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
											Pending Requests ({pendingRequests.length})
										</Typography>
										<TableContainer sx={{ mb: 3 }}>
											<Table size="small">
												<TableHead>
													<TableRow>
														<TableCell sx={{ fontWeight: 'bold' }}>Permission</TableCell>
														<TableCell sx={{ fontWeight: 'bold' }}>Requested</TableCell>
														<TableCell align="center" sx={{ fontWeight: 'bold' }}>Decision</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{pendingRequests.map((req: any, idx: number) => {
														const requestId = req.request_id || req.id;
														const currentDecision = decisions.get(requestId) || 'skip';
														const isChanged = currentDecision !== 'skip';
														
														return (
															<TableRow 
																key={requestId || idx}
																sx={{
																	backgroundColor: isChanged ? 'action.hover' : 'transparent',
																}}
															>
																<TableCell>
																	<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
																		{req.permission_display || req.permission}
																	</Typography>
																</TableCell>
																<TableCell>
																	<Typography variant="body2" color="text.secondary">
																		{req.created_at ? formatDate(req.created_at) : 'N/A'}
																	</Typography>
																</TableCell>
																<TableCell align="center">
																	<RadioGroup
																		row
																		value={currentDecision}
																		onChange={(e) => handleDecisionChange(requestId, e.target.value as DecisionAction)}
																	>
																		<FormControlLabel 
																			value="approve" 
																			control={<Radio size="small" color="success" />} 
																			label="Approve"
																			sx={{ mr: 1 }}
																		/>
																		<FormControlLabel 
																			value="deny" 
																			control={<Radio size="small" color="error" />} 
																			label="Deny"
																			sx={{ mr: 1 }}
																		/>
																		<FormControlLabel 
																			value="skip" 
																			control={<Radio size="small" />} 
																			label="Skip"
																		/>
																	</RadioGroup>
																</TableCell>
															</TableRow>
														);
													})}
												</TableBody>
											</Table>
										</TableContainer>
									</>
								) : null;
							})()}

							{/* Approved Requests List */}
							{(() => {
								const approvedRequests = (selectedGroup.requests || []).filter((req: any) => req.status === 'approved');
								return approvedRequests.length > 0 ? (
									<>
										<Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
											Approved Requests ({approvedRequests.length})
										</Typography>
										<TableContainer sx={{ mb: 3 }}>
											<Table size="small">
												<TableHead>
													<TableRow>
														<TableCell sx={{ fontWeight: 'bold' }}>Permission</TableCell>
														<TableCell sx={{ fontWeight: 'bold' }}>Approved</TableCell>
														<TableCell sx={{ fontWeight: 'bold' }}>Approved By</TableCell>
														<TableCell sx={{ fontWeight: 'bold' }}>Note</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{approvedRequests.map((req: any, idx: number) => (
														<TableRow key={req.request_id || req.id || idx}>
															<TableCell>
																<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
																	{req.permission_display || req.permission}
																</Typography>
															</TableCell>
															<TableCell>
																<Typography variant="body2" color="text.secondary">
																	{req.approved_at ? formatDate(req.approved_at) : 'N/A'}
																</Typography>
															</TableCell>
															<TableCell>
																<Typography variant="body2" color="text.secondary">
																	{req.approver_name || 'N/A'}
																</Typography>
															</TableCell>
															<TableCell>
																<Typography variant="body2" color="text.secondary">
																	{req.approval_note || '—'}
																</Typography>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									</>
								) : null;
							})()}

							{/* Denied Requests List */}
							{(() => {
								const deniedRequests = (selectedGroup.requests || []).filter((req: any) => req.status === 'denied');
								return deniedRequests.length > 0 ? (
									<>
										<Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
											Denied Requests ({deniedRequests.length})
										</Typography>
										<TableContainer sx={{ mb: 3 }}>
											<Table size="small">
												<TableHead>
													<TableRow>
														<TableCell sx={{ fontWeight: 'bold' }}>Permission</TableCell>
														<TableCell sx={{ fontWeight: 'bold' }}>Denied</TableCell>
														<TableCell sx={{ fontWeight: 'bold' }}>Denied By</TableCell>
														<TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{deniedRequests.map((req: any, idx: number) => (
														<TableRow key={req.request_id || req.id || idx}>
															<TableCell>
																<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
																	{req.permission_display || req.permission}
																</Typography>
															</TableCell>
															<TableCell>
																<Typography variant="body2" color="text.secondary">
																	{req.denied_at ? formatDate(req.denied_at) : 'N/A'}
																</Typography>
															</TableCell>
															<TableCell>
																<Typography variant="body2" color="text.secondary">
																	{req.approver_name || 'N/A'}
																</Typography>
															</TableCell>
															<TableCell>
																<Typography variant="body2" color="text.secondary">
																	{req.denial_reason || '—'}
																</Typography>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									</>
								) : null;
							})()}
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setSelectedGroup(null)} variant="outlined">
						Close
					</Button>
					<Button 
						onClick={handleSaveClick} 
						variant="contained" 
						color="primary"
						disabled={!hasChanges}
					>
						Save
					</Button>
				</DialogActions>
			</DialogWrapper>

			{/* Confirmation Dialog */}
			<DialogWrapper open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Confirm Bulk Actions</DialogTitle>
				<DialogContent>
					<Typography variant="body1" sx={{ mb: 2 }}>
						You are about to process the following actions:
					</Typography>
					<Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
						<Typography variant="body2" sx={{ mb: 1 }}>
							<strong>Approve:</strong> {getDecisionCounts().approve} request(s)
						</Typography>
						<Typography variant="body2">
							<strong>Deny:</strong> {getDecisionCounts().deny} request(s)
						</Typography>
					</Box>
					<Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
						Do you want to continue?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDialogOpen(false)} disabled={approveMutation.isPending || denyMutation.isPending} variant="outlined">
						Cancel
					</Button>
					<Button
						onClick={handleBulkProcess}
						variant="contained"
						color="primary"
						disabled={approveMutation.isPending || denyMutation.isPending}
						startIcon={approveMutation.isPending || denyMutation.isPending ? <CircularProgress size={20} /> : null}
					>
						{approveMutation.isPending || denyMutation.isPending ? 'Processing...' : 'Confirm'}
					</Button>
				</DialogActions>
			</DialogWrapper>
		</Box>
	);
};

export default PermissionRequestsTab;
